import { A, useNavigate } from "@solidjs/router";
import { Billing, useBillingClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { HeroHeading } from "@storytell/ui/marketing";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbLoader2, TbLogout, TbMenu2, TbX } from "solid-icons/tb";
import { Match, Show, Suspense, Switch, createSignal } from "solid-js";
import { Avatar } from "~/components/avatar";
import { Logo } from "~/components/brand";
import { UserAccountPopover } from "~/components/common/UserAccountPopover";
import { PromptBarWithStatus, type PromptSubmission } from "~/components/prompt";
import { PromptBarAssetsCTA } from "~/components/prompt/PromptBarAssetsCTA";
import { useWire } from "~/wire";
import { ChatHomepagePlatformDropdown } from "./ChatHomepagePlatformDropdown";

export const ChatHomepage = (props: { onSubmit: (submission: PromptSubmission) => void }) => {
  const [showBanner, setShowBanner] = createSignal(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = createSignal(false);
  const wire = useWire();

  return (
    <main data-testid="initial-welcome-state" class="z-10 flex-1 flex flex-col items-center">
      <div class="flex flex-col items-center w-full">
        <Show when={showBanner()}>
          <div class="py-2 bg-brand px-4 flex items-center gap-4 text-on-brand text-xs rounded-b-xl">
            <span>
              Looking for the previous version of Storytell?{" "}
              <a class="underline font-medium" href="https://app.storytell.ai">
                Click here
              </a>
            </span>
            <button onClick={() => setShowBanner(false)} type="button">
              <TbX />
            </button>
          </div>
        </Show>
        <nav class="w-full flex justify-between items-center pl-10 pr-10 py-5">
          {/* Desktop Navigation */}
          <div class="hidden md:flex gap-10 tracking-wider items-center text-sm">
            <Logo class="mx-auto" size={120} />

            <StPopover
              placement="bottom-start"
              contentProps={{ class: "z-50" }}
              content={<ChatHomepagePlatformDropdown />}
            >
              <StPopoverTrigger class="hover:underline underline-offset-2">Platform</StPopoverTrigger>
            </StPopover>
            <a href="/pricing" class="hover:underline underline-offset-2">
              Pricing
            </a>
            <a href="https://web.storytell.ai/book-a-demo" class="hover:underline underline-offset-2">
              Book Enterprise Demo
            </a>
          </div>

          {/* Mobile Navigation */}
          <div class="md:hidden flex items-center gap-4">
            <Logo class="mx-auto" size={120} />
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen())}
              class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
            >
              <Show when={isMobileMenuOpen()} fallback={<TbMenu2 class="size-6" />}>
                <TbX class="size-6" />
              </Show>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          <Show when={isMobileMenuOpen()}>
            <div class="md:hidden absolute top-20 left-4 right-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
              <div class="p-4 space-y-3">
                <StPopover
                  placement="bottom-start"
                  contentProps={{ class: "z-50" }}
                  content={<ChatHomepagePlatformDropdown />}
                >
                  <StPopoverTrigger class="block w-full text-left hover:underline underline-offset-2">
                    Platform
                  </StPopoverTrigger>
                </StPopover>
                <a href="/pricing" class="block hover:underline underline-offset-2">
                  Pricing
                </a>
                <a href="https://web.storytell.ai/book-a-demo" class="block hover:underline underline-offset-2">
                  Book Demo
                </a>
              </div>
            </div>
          </Show>

          <div class="flex gap-4 items-center h-8">
            <Switch>
              <Match when={!wire.auth?.isReady()}>
                <TbLoader2 class="animate-spin size-6" />
              </Match>
              <Match when={!wire.auth?.isRegistered()}>
                <A href="/auth/login" class="font-medium px-2 py-1">
                  Log in
                </A>
                <A href="/auth/signup" class="font-medium px-4 py-1 rounded text-on-brand bg-brand shadow-xl">
                  Sign up
                </A>
              </Match>
              <Match when={true}>
                <StDropdown
                  modal={false}
                  placement="bottom-end"
                  gutter={5}
                  contentProps={{
                    class: "z-10",
                  }}
                  content={<UserAccountPopover />}
                >
                  <StDropdownTrigger class="cursor-pointer rounded-full">
                    <Avatar
                      class="ring-3"
                      src={wire.auth?.user()?.avatarUrl}
                      name={wire.auth?.user()?.displayName}
                      size="sm"
                    />
                  </StDropdownTrigger>
                </StDropdown>
              </Match>
            </Switch>
          </div>
        </nav>
      </div>
      <div class="text-center w-full">
        <div class="h-14" aria-hidden />
        <div>
          <HeroHeading />
        </div>
        <div class="h-12" aria-hidden />
        {/* Centered prompt bar in initial state */}
        <div class="w-full mx-auto space-y-4 px-4 sm:px-6 md:px-8 lg:px-0">
          {/* TODO: @andi streaming Add streaming state */}
          <PromptBarWithStatus
            onSubmit={props.onSubmit}
            // streamingState={streamingState}
            onStopStreaming={() => {}}
            //  chat={chat}
          />

          <div class="flex justify-center">
            <PromptBarAssetsCTA
              onOpenFilePicker={() => {
                dispatchGlobalEvent("open-file-picker", undefined);
              }}
            />
          </div>
        </div>
      </div>
    </main>
  );
};
